import React, { createContext, useContext, useState, useEffect } from 'react';
import { Project } from '../types';

interface ProjectContextType {
  projects: Project[];
  selectedProject: Project | null;
  setSelectedProject: (project: Project | null) => void;
  setProjects: (projects: Project[]) => void;
  updateProject: (id: string, updates: Partial<Project>) => void;
  isCreateActionModalOpen: boolean;
  setCreateActionModalOpen: (isOpen: boolean) => void;
}

const ProjectContext = createContext<ProjectContextType | undefined>(undefined);

export const ProjectProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [isCreateActionModalOpen, setCreateActionModalOpen] = useState(false);

  // Carrega os projetos do banco local para garantir que o Home funcione
  useEffect(() => {
    const saved = localStorage.getItem('lean_workflow_projects');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // Migração: garantir que projetos antigos sem status sejam tratados como draft
        const migratedProjects = parsed.map((p: Project) => ({
          ...p,
          status: p.status || 'draft'
        }));
        setProjects(migratedProjects);
        // Salvar projetos migrados de volta ao localStorage
        localStorage.setItem('lean_workflow_projects', JSON.stringify(migratedProjects));
      } catch (e) {
        console.error("Erro ao carregar projetos locais", e);
      }
    }
  }, []);

  const updateProject = (id: string, updates: Partial<Project>) => {
    setProjects(prev => {
      const newList = prev.map(p => p.id === id ? { ...p, ...updates } : p);
      localStorage.setItem('lean_workflow_projects', JSON.stringify(newList));
      return newList;
    });
    
    if (selectedProject?.id === id) {
      setSelectedProject(prev => prev ? { ...prev, ...updates } : null);
    }
  };

  return (
    <ProjectContext.Provider value={{ 
      projects, 
      selectedProject, 
      setSelectedProject, 
      setProjects, 
      updateProject,
      isCreateActionModalOpen,
      setCreateActionModalOpen
    }}>
      {children}
    </ProjectContext.Provider>
  );
};

export const useProjectContext = () => {
  const context = useContext(ProjectContext);
  if (context === undefined) {
    throw new Error('useProjectContext deve ser usado dentro de um ProjectProvider');
  }
  return context;
};